import { Outlet, Link, useLocation, Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import logoSvg from '../assets/logo.svg'
import Spinner from '../components/shared/Spinner'
import SkipLink from '../components/shared/SkipLink'

const coreLinks = [
  { path: '/admin', label: 'Dashboard', icon: '📊' },
  { path: '/admin/users', label: 'Users', icon: '👥' },
  { path: '/admin/transactions', label: 'Transactions', icon: '💰' },
  { path: '/admin/companies', label: 'Companies', icon: '🏢' },
  { path: '/admin/audit', label: 'Audit Logs', icon: '📋' },
  { path: '/admin/staff', label: 'Admin Staff', icon: '🔑' },
]

const systemLinks = [
  { path: '/admin/feature-flags', label: 'Feature Flags', icon: '🚩' },
  { path: '/admin/system', label: 'System', icon: '⚙️' },
]

export default function AdminLayout() {
  const location = useLocation()
  const { user, isLoading, isAdmin, logout } = useAuth()

  if (isLoading) {
    return (
      <div className="dashboard-loading" role="status" aria-label="Loading admin panel">
        <Spinner size="large" label="Loading..." />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="dashboard">
      <SkipLink href="#admin-content" />

      <aside className="dashboard-sidebar" aria-label="Admin navigation">
        <Link to="/" className="dashboard-logo" aria-label="PlanScan home">
          <img src={logoSvg} alt="PlanScan" />
        </Link>

        <nav className="dashboard-nav" aria-label="Admin menu">
          {coreLinks.map((link) => {
            const isActive = link.path === '/admin'
              ? location.pathname === '/admin'
              : location.pathname.startsWith(link.path)
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`dashboard-nav-link ${isActive ? 'active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="dashboard-nav-icon" aria-hidden="true">{link.icon}</span>
                {link.label}
              </Link>
            )
          })}

          <span className="dashboard-nav-section-label">System</span>

          {systemLinks.map((link) => {
            const isActive = location.pathname.startsWith(link.path)
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`dashboard-nav-link ${isActive ? 'active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="dashboard-nav-icon" aria-hidden="true">{link.icon}</span>
                {link.label}
              </Link>
            )
          })}
        </nav>

        <div className="dashboard-nav" style={{ marginTop: 'auto' }}>
          <Link
            to="/dashboard"
            className="dashboard-nav-link"
          >
            <span className="dashboard-nav-icon" aria-hidden="true">🏠</span>
            Back to Dashboard
          </Link>
        </div>

        <div className="dashboard-user">
          <span className="dashboard-user-email">{user.email}</span>
          <button
            onClick={logout}
            className="dashboard-logout"
            aria-label="Sign out of your account"
          >
            Sign Out
          </button>
        </div>
      </aside>

      <main id="admin-content" className="dashboard-main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  )
}
